import React from 'react';
import PropTypes from 'prop-types';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import {useDispatch, useSelector} from 'react-redux';
import ButtonOutline from 'components/buttons/ButtonOutline/styles';
import {
  FETCH_LARGE_ORDER_INFO,
  HOLD_NEW_ORDERS,
  PREPEND_LARGE_ORDER
} from 'store/global/ordersV2/action';
import {
  formatCourierNames,
  isLargeDeliveryOrder
} from 'helpers/functionUtils/ordersUtilsV2';
import {hasLargeDeliveryAccess} from 'helpers/constants/restrictions';
import {isSauceDriverPending} from 'helpers/functionUtils/hybridDelivery';
import {ORDER, ORDER_STATUS} from 'helpers/constants/orders_v2';
import {
  LargeDeliveryBannerContent,
  LargeDeliveryBannerIcon,
  LargeDeliveryBannerSubtitle,
  LargeDeliveryBannerTitle,
  LargeDeliveryBannerWrap
} from './styles';

const HIDDEN_STATUSES = [ORDER_STATUS.CANCELLED, ORDER_STATUS.COMPLETED];

const LargeDeliveryBanner = ({order}) => {
  const dispatch = useDispatch();
  const activeLocation = useSelector((state) => state.user?.activeLocation);
  const serviceFeatures = activeLocation?.AllowedServiceFeatures || [];
  const {badOrders, badOrdersLoading} = useSelector(
    (state) => state.orderDetailsV2Reducer
  );

  if (
    !hasLargeDeliveryAccess(serviceFeatures, activeLocation) ||
    !isLargeDeliveryOrder(order) ||
    // No extra drivers while Sauce has yet to book the main one: the merchant can
    // still switch the order to their own driver.
    isSauceDriverPending(order, serviceFeatures) ||
    badOrdersLoading
  ) {
    return null;
  }

  const bookedOrders = badOrders || [];
  const hasBookedDrivers = bookedOrders.length > 0;

  if (
    !hasBookedDrivers &&
    HIDDEN_STATUSES.includes(order[ORDER.ORDER_STATUS])
  ) {
    return null;
  }

  if (hasBookedDrivers) {
    const courierNames = formatCourierNames(
      bookedOrders.map((item) => item?.courier?.name)
    );
    const count = bookedOrders.length;

    return (
      <LargeDeliveryBannerWrap className='success'>
        <LargeDeliveryBannerContent>
          <LargeDeliveryBannerIcon className='success'>
            <CheckCircleIcon />
          </LargeDeliveryBannerIcon>
          <LargeDeliveryBannerTextBlock
            title={`${count} additional driver${count === 1 ? '' : 's'} booked`}
            subtitle={
              courierNames
                ? `${courierNames} ${
                    count === 1 ? 'is' : 'are'
                  } assigned to this order`
                : null
            }
          />
        </LargeDeliveryBannerContent>
      </LargeDeliveryBannerWrap>
    );
  }

  const onAddDrivers = () => {
    dispatch(HOLD_NEW_ORDERS());
    dispatch(PREPEND_LARGE_ORDER(order));
    dispatch(FETCH_LARGE_ORDER_INFO(order[ORDER.ID]));
  };

  return (
    <LargeDeliveryBannerWrap className='warning'>
      <LargeDeliveryBannerContent>
        <LargeDeliveryBannerIcon className='warning'>
          <ReportProblemIcon />
        </LargeDeliveryBannerIcon>
        <LargeDeliveryBannerTitle>
          This order is larger than usual — extra drivers may help it arrive on
          time.
        </LargeDeliveryBannerTitle>
      </LargeDeliveryBannerContent>
      <ButtonOutline className='ButtonSmall' onClick={onAddDrivers}>
        Add drivers
      </ButtonOutline>
    </LargeDeliveryBannerWrap>
  );
};

const LargeDeliveryBannerTextBlock = ({title, subtitle}) => (
  <div>
    <LargeDeliveryBannerTitle>{title}</LargeDeliveryBannerTitle>
    {subtitle && (
      <LargeDeliveryBannerSubtitle>{subtitle}</LargeDeliveryBannerSubtitle>
    )}
  </div>
);

LargeDeliveryBannerTextBlock.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string
};

LargeDeliveryBannerTextBlock.defaultProps = {
  subtitle: null
};

LargeDeliveryBanner.propTypes = {
  order: PropTypes.object.isRequired
};

export default LargeDeliveryBanner;
